// Équipe : l'effectif.
//  - staff : ajouter / modifier / retirer une joueuse, l'inviter par e-mail,
//    inviter ses parents, délivrer son code perso ; les indisponibilités en cours (périodes et règles de la
//    semaine, D-114), en ajouter une pour une joueuse ;
//  - parent : délivrer le code perso de son enfant (D-010) ;
//  - joueuse : l'effectif (prénoms, surnoms, numéros).
//  - staff (coachs de l'équipe, responsable technique, bureau) : les contacts de la joueuse et de
//    ses parents (D-103), jamais visibles des autres familles ; le coach affiché par son prénom ;
//  - staff : la ⚙ de l'équipe (D-148, src/ui/desist.ts) : prévenir d'une absence, désistements tardifs.
//  - staff de l'équipe et responsable technique : la fiche « Pour les tirages » sur la carte d'une joueuse (D-176), le
//    reste de la carte replié (Ses infos, Connexion, Surclassements, Parents).
import { renderTeamPhoto } from './team-photo';
import {
  personSurclassements, setSurclassement, teamLicenceStatus, type SurclassementRow,
  addTeamPlayer, invite, managedPeople, personAccess, personGuardians, teamRoster, teamStaffAccounts,
  toDomainRule, updateMembership, updatePerson, type Person, type RosterEntry,
  createSquad, deleteSquad, setMemberSquads, personInviteLink, clubStaffNames,
} from '../lib/api';
import { personContacts, type Contacts } from '../lib/reach-api';
import { linksFor } from '../lib/club-links';
import './reach.css';
import { inSquad, toggleSquad } from '../domain/squads';
import { localDate } from '../domain/schedule';
import { categoriesAbove } from '../domain/categories';
import { STATUS_LABEL as SURC_STATUS } from '../domain/surclassement';
import { agree, displayName, playerCount, playerNoun, type TeamGender } from '../domain/labels';
import { children, hatShowing, modOn, refreshContext, switchHat, teamOf, workTeams, type Ctx } from './context';
import { renderTeamBirthdays } from './group-life';
import { shortDate } from './format';
import { renderTeamMatches } from './team-matches';
import { renderSquadLocks } from './match-day';
import { renderPlaybook } from './playbook';
import { renderWaitingRoom, teamWaits } from './gratuite';
import { renderSocialEditor } from './social';
import { $, $$, confirmTwice, html, mount, openSheet, raw, toast, armed, type Html } from './dom';
import { drawProfile } from '../lib/tirage-api';
import { readProfile } from '../domain/tirage';
import { ficheHtml, wireFiche } from './tirage';
import { openShare } from './share';
import './indispos.css';
import { ruleCurrent, ruleText, untilText } from '../domain/availability';
import { loadAvail, type AvailData } from './availability-data';
import { openUnavSheet, periodRange } from './unavailability';
import { loadAgenda } from './agenda';
import { occOf } from './availability-data';
import { isMinorPerson } from '../domain/minors';
import { renderTeamFollow } from './suivre';

const ERRORS: Record<string, string> = {
  has_email_account: 'Cette personne se connecte déjà avec son adresse e-mail.',
  has_code_account: "Elle a un code perso : retire-le d'abord pour passer à l'e-mail.",
  already_linked: 'Cette fiche est déjà rattachée à un autre compte.',
  account_in_use: 'Cette adresse est déjà celle d\'une autre personne du club.',
  forbidden: "Tu n'as pas le droit de faire ça.",
  input: 'Adresse e-mail invalide.',
  no_code: 'Pas de code à retirer.',
};
const err = (code?: string) => ERRORS[code ?? ''] ?? 'Opération impossible. Réessaie.';

/** `open` : la carte d'une joueuse à ouvrir tout de suite (son identifiant, ou « first » : la première de l'effectif). */
export async function renderTeam(root: HTMLElement, ctx: Ctx, teamId: string, open?: string): Promise<void> {
  const otherHat = hatShowing(ctx, teamId);   // casquettes (D-072)
  if (otherHat) { switchHat(ctx, otherHat); window.dispatchEvent(new HashChangeEvent('hashchange')); return; }
  const team = teamOf(ctx, teamId);
  // L'équipe gratuite (D-166) : une équipe qui attend l'accord du club a sa salle d'attente.
  const waits = team && ctx.staffOf.has(teamId) ? teamWaits(ctx, teamId) : null;
  if (waits) return renderWaitingRoom(root, ctx, teamId, waits);
  if (!team) {
    mount(root, html`<section class="screen"><a class="back" href="#/" data-back>‹ Retour</a><div class="empty"><p class="empty-title">Équipe introuvable.</p></div></section>`);
    return;
  }
  const staff = ctx.staffOf.has(teamId);
  const roster = (await teamRoster(teamId)).filter(r => r.kind === 'member');
  // Licences (D-029) : le staff voit qui est licencié (si le club suit les dossiers).
  // Licences (D-029) : le staff voit qui peut jouer (statut FFBB), rien de plus.
  const lic = new Map(staff && modOn(ctx, 'licences', team.club_id) ? (await teamLicenceStatus(teamId).catch(() => [])).map(x => [x.person_id, x.ffbb_status]) : []);
  const tracked = [...lic.values()].some(Boolean);   // le club suit-il les dossiers ?
  const licChip = (pid: string) => {
    if (!tracked) return '';
    const st = lic.get(pid);
    const word = agree(team.gender, 'licencié', 'licenciée');
    return st === 'validee' ? html` <span class="chip chip-ok">${word}</span>`
      : st === 'envoyee' ? html` <span class="chip chip-mute">licence en cours</span>`
      : html` <span class="chip chip-ko">pas ${word}</span>`;
  };
  const kidIds = new Set(children(ctx).map(p => p.id));
  const manageable = roster.filter(r => staff || kidIds.has(r.person_id)).map(r => r.person_id);
  const [sheetsList, coaches, unavRows, staffNames, links] = await Promise.all([
    managedPeople(manageable),
    staff ? teamStaffAccounts(teamId) : Promise.resolve([]),
    staff ? loadAvail() : Promise.resolve(null as AvailData | null),
    staff ? clubStaffNames(team.club_id).catch(() => new Map<string, string>()) : Promise.resolve(new Map<string, string>()),
    linksFor(team.club_id),
  ]);
  // Le coach par son prénom (son profil, sinon sa fiche), jamais par son adresse.
  const coachName = (c: { user_id: string; email: string }) => {
    const n = staffNames.get(c.user_id);
    return n && n !== 'Compte' ? n : (c.email.split('@')[0] ?? '').replace(/^./, x => x.toUpperCase());
  };
  const sheets = new Map(sheetsList.map(p => [p.id, p] as [string, Person]));
  const today = localDate(new Date());
  // Les indisponibilités en cours (D-114) : les règles de la semaine, puis les périodes en cours ou à venir.
  const inTeam = (pid: string) => roster.some(r => r.person_id === pid);
  const rName = (pid: string) => { const r = roster.find(x => x.person_id === pid); return r ? displayName(r) : ''; };
  const rules = (unavRows?.ruleRows ?? []).filter(r => inTeam(r.person_id) && ruleCurrent(toDomainRule(r), today));   // dans l'ordre où elles ont été posées
  const unav = (unavRows?.periodRows ?? []).filter(u => u.ends_on >= today && inTeam(u.person_id));
  const inline = (x: string) => (x.length > 1 && x[1] === x[1]!.toLowerCase() ? x.charAt(0).toLowerCase() + x.slice(1) : x);
  const noun = playerNoun(team.gender);
  const reload = () => renderTeam(root, ctx, teamId);
  // Équipes engagées du groupe (D-042) : SF1, SF2…
  // Leurs compétitions (championnat, coupe, phases finales…) : « Compétitions de la saison » (D-090).
  const squads = ctx.squads.filter(s => s.team_id === teamId).sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
  const mates = workTeams(ctx);
  const sqChips = (r: RosterEntry) => squads.length > 1 ? html`<span class="sq-chips" role="group" aria-label="Équipes de ${displayName(r)}">${squads.map(q => {
    const on = inSquad(r.squad_ids, q.id);
    return staff ? html`<button type="button" class="sq-chip ${on ? 'on' : ''}" data-sq="${q.id}" data-sqp="${r.person_id}" aria-pressed="${String(on)}">${on ? '✓ ' : ''}${q.name}</button>`
      : on ? html`<span class="sq-chip on">${q.name}</span>` : '';
  })}</span>` : '';

  const access = (pid: string) => {
    const p = sheets.get(pid);
    if (!p) return '';
    if (p.user_id && !p.login_handle) return html`<span class="acc acc-mail" title="Se connecte par e-mail">e-mail</span>`;
    if (p.login_handle) return html`<span class="acc acc-code" title="Code perso">code</span>`;
    return html`<span class="acc acc-none">sans accès</span>`;
  };

  mount(root, html`
    <section class="screen">
      <a class="back" href="#/" data-back>‹ Retour</a>
      ${mates.length > 1 ? html`<nav class="team-strip" aria-label="Mes équipes">${mates.map(t => html`<a class="team-pill ${t.id === teamId ? 'on' : ''}" href="#/t/${t.id}">${t.name}</a>`)}</nav>` : ''}
      <header class="screen-head tph-head ${staff ? 'nv-has-gear' : ''}">
        ${staff ? html`<a class="nv-gear" href="#/t/${team.id}/reglages" aria-label="Réglages de l’équipe" data-guide="team-gear"><span class="ti ti-gear" aria-hidden="true"></span></a>` : ''}
        <div id="team-photo"></div>
        <div>
        <p class="eyebrow">${team.category ?? 'Équipe'}${ctx.coachOf.has(teamId) ? ' · tu encadres cette équipe' : ''}${squads.length > 1 ? ` · ${squads.map(q => q.name).join(' + ')}` : ''}</p>
        <h2>${team.name}</h2>
        <p class="muted">${playerCount(team.gender, roster.length)}${coaches.length ? html` · ${coaches.length > 1 ? 'coachs' : 'coach'} : ${coaches.map(coachName).join(', ')}` : ''}</p>
        <div id="team-follow"></div>
        </div>
      </header>
      <div id="team-bday"></div>
      <div id="team-tournois"></div>
      <div id="team-maillots"></div>

      ${staff ? html`
        <section class="block" data-guide="team-indispos">
          <div class="block-head"><h3>Les indisponibilités en cours</h3><button type="button" class="btn btn-ghost btn-sm" id="b-ix-add">+ Ajouter</button></div>
          ${rules.length || unav.length ? html`<ul class="un-list">${rules.map(r => html`
            <li class="un-row ix-rule" data-ix-edit="${r.id}" tabindex="0" role="button" aria-label="Modifier la règle de ${rName(r.person_id)}">
              <div class="un-dates">${rName(r.person_id)} · ${ruleText(toDomainRule(r))} <span class="chip ix-week">chaque semaine</span></div>
              <div class="un-meta"><span>${untilText(toDomainRule(r), true)}</span>${r.note ? html`<span class="muted">${inline(r.note)}</span>` : ''}</div></li>`)}${unav.map(u => html`
            <li class="un-row"><div class="un-dates">${rName(u.person_id)} · ${u.starts_on <= today ? `jusqu’au ${shortDate(u.ends_on)}` : u.starts_on === u.ends_on ? `le ${shortDate(u.starts_on)}` : `du ${periodRange(u.starts_on, u.ends_on)}`}</div>
              <div class="un-meta">${u.is_medical ? html`<span class="tag-med">médical</span>` : ''}${u.note ? html`<span class="muted">${inline(u.note)}</span>` : ''}</div></li>`)}</ul>`
            : html`<p class="muted">Aucune en ce moment.</p>`}
          <p class="hint">Une seule notification quand une règle est créée. Les relances et la répartition des doubles matchs en tiennent compte.</p>
        </section>` : ''}

      <section class="block">
        <div class="block-head">
          <h3>Effectif</h3>
          ${staff ? html`<span class="btn-row"><a class="btn btn-ghost btn-sm" href="#/inviter/equipe/${team.id}" data-guide="team-invite">Inviter l’équipe</a><button type="button" class="btn btn-primary btn-sm" id="b-add">+ ${noun.charAt(0).toUpperCase() + noun.slice(1)}</button></span>` : ''}
        </div>
        ${roster.length ? html`<ul class="pl-list" data-guide="team-roster">
          ${roster.map(r => {
            const open = manageable.includes(r.person_id);
            return html`
            <li class="pl-row ${open ? 'pl-open' : ''}" data-pid="${r.person_id}" ${open ? html`data-open="${r.person_id}" tabindex="0" role="button"` : ''}>
              <span class="num">${r.jersey_number ?? ''}</span>
              <span class="pl-name">${displayName(r)}${r.nickname ? html` <span class="muted small">${r.first_name}</span>` : ''}${licChip(r.person_id)}${sqChips(r)}</span>
              ${open ? access(r.person_id) : ''}
              ${open ? html`<span class="occ-go" aria-hidden="true">›</span>` : ''}
            </li>`;
          })}
        </ul>` : html`<p class="muted">Pas encore de ${noun} dans l'effectif.</p>`}
      </section>
      ${staff || squads.length ? html`
        <section class="block">
          <h3>Équipes engagées${squads.length ? html` <span class="muted small">${squads.length}</span>` : ''}</h3>
          ${squads.length ? html`<ul class="ce-rows">${squads.map(q => html`
            <li><span><strong>${q.name}</strong></span>
              ${staff ? html`<button type="button" class="link-x" data-sq-del="${q.id}" aria-label="Retirer ${q.name}">retirer</button>` : ''}</li>`)}</ul>` : ''}
          ${staff ? html`
            <form class="inline-form" id="f-squad" novalidate>
              <input name="name" maxlength="20" placeholder="${squads.length ? 'ex. SF3' : 'ex. SF1'}" aria-label="Nom de l'équipe engagée">
              <button class="btn btn-ghost btn-sm" type="submit">Ajouter</button>
            </form>
            <p class="hint">Un groupe, plusieurs équipes en compétition (SF1, SF2) : l'effectif et les entraînements sont communs, chaque match ne convoque que les ${noun}s de son équipe. Dans l'effectif, touche SF1 / SF2 pour dire qui joue où (par défaut : partout). Qui joue quelle compétition : « Compétitions de la saison », plus bas.</p>` : ''}
        </section>` : ''}
      <div id="team-locks"></div>
      ${modOn(ctx, 'defis') || staff ? html`<a class="card card-row" href="#/defis/${team.id}"><span class="play-thumb" aria-hidden="true"><span class="ti ti-trophy"></span></span>
        <span class="card-main"><span class="card-title">Défis et gages</span><span class="muted small">${modOn(ctx, 'defis') ? 'Défis, podiums, gages' : 'Pas encore utilisé par le club'}</span></span><span class="occ-go" aria-hidden="true">›</span></a>` : ''}
      <a class="card card-row" href="#/basket/plays/${team.id}"><span class="play-thumb" aria-hidden="true"><span class="ti ti-plays"></span></span>
        <span class="card-main"><span class="card-title">Plays de l'équipe</span><span class="muted small">Les systèmes, animés</span></span><span class="occ-go" aria-hidden="true">›</span></a>
      <div id="team-matches" class="screen"></div>
      <div id="team-social"></div>
    </section>`);
  void renderTeamPhoto(root.querySelector<HTMLElement>('#team-photo')!, team, staff).catch(() => undefined);
  // D-169 : les accords des parents d'un coup d'œil (une icône photo, une icône voiture, seulement s'il y a une restriction).
  if (staff) void import('./accords-club').then(m => m.renderRosterAccords(root, teamId, team.gender)).catch(() => undefined);
  // D-149 : le cadre de la photo de chacun (les skins des joueuses) ; éteint, l'effectif reste tel quel.
  if (modOn(ctx, 'skins', team.club_id)) void import('./skins').then(m => m.decorateRoster(root, ctx, teamId, team.gender)).catch(() => undefined);
  // D-177 : le tournoi interne de l'équipe (le coach l'organise, si le bureau le permet).
  if (staff && modOn(ctx, 'tournois')) { const el = root.querySelector<HTMLElement>('#team-tournois'); if (el) void import('./tournoi').then(m => m.renderTeamTournaments(el, ctx, teamId)).catch(() => undefined); }
  // D-188 : « Qui lave les maillots ? » (le coach active ; une fois actif, le coach et les familles de l'équipe ; rien pour le bureau).
  { const el = root.querySelector<HTMLElement>('#team-maillots'); if (el) void import('./maillots').then(m => m.renderTeamJerseys(el, ctx, teamId)).catch(() => undefined); }
  void renderTeamBirthdays(root.querySelector<HTMLElement>('#team-bday')!, ctx, team);   // D-151 : « Joyeux anniversaire … ! » le jour J
  if (modOn(ctx, 'page', team.club_id)) void renderTeamFollow(root.querySelector<HTMLElement>('#team-follow')!, team.club_id, team.id);   // « ☆ Suivre » (D-110), avec la page du club (D-122)

  void renderPlaybook($(root, '#team-playbook')!, team, staff).catch(() => undefined);
  void renderTeamMatches($(root, '#team-matches')!, ctx, team, staff).catch(() => undefined);
  // Joueuses brûlées du groupe (D-092) : staff, quand le groupe engage plusieurs équipes.
  void renderSquadLocks($(root, '#team-locks')!, ctx, team, roster).catch(() => undefined);
  void renderSocialEditor($(root, '#team-social')!, team.club_id, team.id, 'Réseaux de l’équipe', staff).catch(() => undefined);

  // --- Ajouter une joueuse ---------------------------------------------------
  // Le coach pose une indisponibilité (période ou règle de la semaine) pour une joueuse de l'équipe (D-114).
  const openIx = async (ruleId?: string) => {
    const ag = await loadAgenda(ctx, 42).catch(() => null);
    const rule = ruleId ? rules.find(r => r.id === ruleId) : undefined;
    openUnavSheet({
      people: roster.map(r => ({ id: r.person_id, name: displayName(r), tag: null, gender: team.gender, minor: isMinorPerson(sheets.get(r.person_id)), self: false })),
      items: (ag?.items ?? []).filter(it => it.ev.team_id === teamId),
      convoked: (it, pid) => !it.match?.squad_id || inSquad(roster.find(r => r.person_id === pid)?.squad_ids, it.match.squad_id),
      occ: it => occOf(it.ev, it.occ, it.match, ag?.leads.get(team.club_id) ?? { arrive: 60, home: 45 }),
      rule, staff: true, onDone: () => void reload(),
    });
  };
  $(root, '#b-ix-add')?.addEventListener('click', () => void openIx());
  for (const li of $$<HTMLElement>(root, '[data-ix-edit]')) {
    li.addEventListener('click', () => void openIx(li.dataset.ixEdit));
    li.addEventListener('keydown', e => { if (e.key === 'Enter') void openIx(li.dataset.ixEdit); });
  }
  $(root, '#b-add')?.addEventListener('click', () => {
    openSheet(agree(team.gender, 'Nouveau joueur', 'Nouvelle joueuse'), html`
      <form class="form" id="f-add" novalidate>
        <div class="row-2">
          <label class="field"><span>Prénom</span><input name="first_name" required maxlength="60" autocapitalize="words"></label>
          <label class="field"><span>Nom</span><input name="last_name" maxlength="60" autocapitalize="words"></label>
        </div>
        <div class="row-2">
          <label class="field"><span>Surnom (facultatif)</span><input name="nickname" maxlength="24" placeholder="s'affiche à la place du prénom"></label>
          <label class="field"><span>N° de maillot</span><input name="jersey" type="number" inputmode="numeric" min="0" max="99"></label>
        </div>
        <label class="field"><span>Date de naissance (facultatif)</span><input name="birth_date" type="date" max="${today}"></label>
        <p class="hint">Visible du staff seulement. Deux ${playerNoun(team.gender, 2)} peuvent porter le même numéro : le surnom les distingue.</p>
        <p class="form-msg" role="alert"></p>
        <button class="btn btn-primary" type="submit">Ajouter à l'effectif</button>
      </form>`, (sheet, close) => {
      const f = $<HTMLFormElement>(sheet, '#f-add')!;
      f.addEventListener('submit', async (e) => {
        e.preventDefault();
        const d = new FormData(f);
        const first = String(d.get('first_name') ?? '').trim();
        if (!first) { $(f, '.form-msg')!.textContent = 'Le prénom est obligatoire.'; return; }
        const jersey = String(d.get('jersey') ?? '');
        try {
          await addTeamPlayer(teamId, {
            first_name: first, last_name: String(d.get('last_name') ?? ''), nickname: String(d.get('nickname') ?? ''),
            birth_date: String(d.get('birth_date') ?? ''), jersey: jersey === '' ? null : Number(jersey),
          });
          close(); toast(`${first} ${agree(team.gender, 'ajouté', 'ajoutée')} à l'effectif`);
          await reload();
        } catch { $(f, '.form-msg')!.textContent = 'Ajout impossible. Réessaie.'; }
      });
    });
  });

  // --- Fiche d'une joueuse ---------------------------------------------------
  const openPlayer = async (pid: string) => {
    const p = sheets.get(pid)!;
    const r = roster.find(x => x.person_id === pid)!;
    const [guardians, contacts] = staff
      ? await Promise.all([personGuardians(pid), personContacts(pid).catch(() => null as Contacts | null)])
      : [[], null as Contacts | null];
    // L'e-mail importé (FBI), déjà rempli : le sien pour un adulte, celui d'un parent pas encore relié.
    const ownMail = contacts && !contacts.minor ? contacts.emails[0] ?? '' : '';
    const parentMail = contacts?.parents.find(g => !g.account && g.email)?.email ?? '';
    // Surclassements de la saison (D-026) : catégories au-dessus de celle de l'équipe.
    const surcOptions = staff && team.season_id ? categoriesAbove(team.category) : [];
    let surc = new Map<string, SurclassementRow>();
    const loadSurc = async () => {
      surc = new Map((await personSurclassements(pid).catch(() => [])).filter(x => x.season_id === team.season_id).map(x => [x.category, x]));
    };
    if (surcOptions.length) await loadSurc();
    const surcChip = (c: string) => {
      const x = surc.get(c);
      return html`<button type="button" class="chip-toggle surc-${x?.status ?? 'none'}" data-surc="${c}" aria-pressed="${String(!!x && x.status !== 'refuse')}">${c}${x ? html` <small>${SURC_STATUS[x.status].toLowerCase()}${x.effective_on && x.status !== 'valide' ? ` le ${shortDate(x.effective_on)}` : ''}</small>` : ''}</button>`;
    };
    const name = displayName(p);
    const hasMail = !!(p.user_id && !p.login_handle);
    // D-169 : ses accords (photos, transport) en une ligne, pour le staff.
    const akLine = staff ? await import('./accords-club').then(m => m.personAccordsLine(teamId, pid)).catch(() => html``) : html``;

    // D-176 : la fiche « Pour les tirages », au staff de l'équipe et au responsable technique ; le reste de la carte replié.
    const canDraw = ctx.full.coachOf.has(teamId);
    const prof = canDraw ? await drawProfile(teamId, pid).catch(() => readProfile(null)) : null;
    const editHtml = html`${staff ? html`
        <form class="form" id="f-edit" novalidate>
          <div class="row-2">
            <label class="field"><span>Prénom</span><input name="first_name" value="${p.first_name}" maxlength="60" required></label>
            <label class="field"><span>Nom</span><input name="last_name" value="${p.last_name ?? ''}" maxlength="60"></label>
          </div>
          <div class="row-2">
            <label class="field"><span>Surnom</span><input name="nickname" value="${p.nickname ?? ''}" maxlength="24"></label>
            <label class="field"><span>N°</span><input name="jersey" type="number" min="0" max="99" value="${r.jersey_number ?? ''}"></label>
          </div>
          <label class="field"><span>Date de naissance</span><input name="birth_date" type="date" value="${p.birth_date ?? ''}" max="${today}"></label>
          <button class="btn btn-ghost" type="submit">Enregistrer</button>
        </form>` : ''}`;
    const connHtml = html`      <section class="sheet-sec">
        <h4>Connexion</h4>
        ${hasMail ? html`<p class="muted">Se connecte avec son adresse e-mail.</p>`
          : p.login_handle ? html`
            <p class="muted">Code perso · identifiant <strong class="mono">${p.login_handle}</strong></p>
            <div class="btn-row">
              <button type="button" class="btn btn-ghost btn-sm" id="b-issue">Nouveau code</button>
              <button type="button" class="btn btn-danger-ghost btn-sm" id="b-revoke">Retirer l'accès</button>
            </div>`
          : html`
            <p class="muted">Pas encore d'accès à l'app.</p>
            ${staff ? html`
              <button type="button" class="btn btn-primary btn-sm" data-plink="self">Son lien (WhatsApp, SMS)</button>
              <form class="inline-form" id="f-mail" novalidate>
                <input name="email" type="email" inputmode="email" value="${ownMail}" placeholder="ou son e-mail (adulte)" aria-label="E-mail de ${name}">
                <button class="btn btn-ghost btn-sm" type="submit">Inviter</button>
              </form>` : ''}
            <button type="button" class="btn btn-ghost btn-sm" id="b-issue">Créer un code perso (sans e-mail)</button>`}
        <div id="code-out"></div>
      </section>`;
    const surcHtml = html`      ${surcOptions.length ? html`
        <section class="sheet-sec">
          <h4>Surclassements · cette saison</h4>
          <div class="chip-toggles" id="surc-chips">${surcOptions.map(surcChip)}</div>
          <p class="hint">Cocher une catégorie, c'est demander le surclassement : la famille reçoit ce qu'il faut fournir, le bureau le dépose au comité. ${agree(team.gender, 'Il', 'Elle')} pourra être ${agree(team.gender, 'convoqué', 'convoquée')} en renfort dans cette catégorie dès le dépôt.</p>
        </section>` : ''}`;
    const parentsHtml = staff ? html`        <section class="sheet-sec">
          <h4>Parents</h4>
          ${guardians.length ? html`<ul class="g-list">${guardians.map(g => html`
            <li><span>${g.email}</span><span class="muted small">${g.relation ?? ''}${g.consent_at ? ' · consentement donné' : ' · consentement en attente'}</span></li>`)}</ul>`
            : html`<p class="muted">Aucun parent rattaché.</p>`}
          <button type="button" class="btn btn-ghost btn-sm" data-plink="parent">Lien pour ses parents (WhatsApp, SMS)</button>
          <form class="inline-form" id="f-parent" novalidate>
            <input name="email" type="email" inputmode="email" value="${parentMail}" placeholder="e-mail du parent" aria-label="E-mail du parent">
            <select name="relation" aria-label="Lien"><option>mère</option><option>père</option><option value="">autre</option></select>
            <button class="btn btn-ghost btn-sm" type="submit">Inviter</button>
          </form>
        </section>` : html``;
    const leaveHtml = staff ? html`<button type="button" class="btn btn-danger-ghost" id="b-leave">Retirer de l'effectif</button>` : html``;
    const born = p.birth_date ? `${agree(team.gender, 'né', 'née')} en ${p.birth_date.slice(0, 4)}` : null;
    const licTxt = tracked ? (lic.get(pid) === 'validee' ? agree(team.gender, 'licencié', 'licenciée') : lic.get(pid) === 'envoyee' ? 'licence envoyée' : `pas ${agree(team.gender, 'licencié', 'licenciée')}`) : null;
    const connSub = hasMail ? 'E-mail' : p.login_handle ? 'Code perso' : 'Pas encore d’accès';
    const parSub = guardians.length ? `${guardians[0]!.relation || 'parent'}${guardians[0]!.consent_at ? ' · consentement donné' : ' · consentement en attente'}${guardians.length > 1 ? ` · +${guardians.length - 1}` : ''}` : 'Aucun rattaché';
    const fold = (title: string, sub: string, inner: Html, guide = '') => html`<details class="tr-fold" ${guide ? raw(`data-guide="${guide}"`) : ''}><summary><b>${title}</b><span>${sub} ›</span></summary><div class="tr-fold-in">${inner}</div></details>`;

    openSheet(name, canDraw && prof ? html`
      <div class="tr-id"><span class="n">${r.jersey_number ?? name.charAt(0).toUpperCase()}</span><span><b>${name} · ${team.name}</b><small>${[r.jersey_number !== null ? `N° ${r.jersey_number}` : null, born, licTxt].filter(Boolean).join(' · ') || playerNoun(team.gender)}</small></span></div>
      ${akLine}
      ${ficheHtml(team, name, prof)}
      ${fold('Ses infos', 'Prénom, nom, n°', html`${editHtml}${contacts ? contactsBlock(contacts, team.gender) : ''}`)}
      ${fold('Connexion', connSub, connHtml)}
      ${surcOptions.length ? fold('Surclassements', [...surc.values()].length ? `${[...surc.values()].length} cette saison` : 'Aucun', surcHtml) : ''}
      ${staff ? fold('Parents', parSub, parentsHtml) : ''}
      ${leaveHtml}` : html`
      ${akLine}
      ${editHtml}
      ${contacts ? contactsBlock(contacts, team.gender) : ''}
      ${connHtml}
      ${surcHtml}
      ${parentsHtml}
      ${leaveHtml}
    `, (sheet, close) => {
      if (canDraw && prof) wireFiche(sheet, team, pid, prof);
      const showCode = (handle: string, code: string) => mount($(sheet, '#code-out')!, html`
        <div class="code-card">
          <div><span class="muted small">Identifiant</span><strong class="mono">${handle}</strong></div>
          <div><span class="muted small">Code</span><strong class="mono code-big">${code.slice(0, 3)} ${code.slice(3)}</strong></div>
        </div>
        <p class="warn">Note-le ou transmets-le maintenant : il ne sera plus jamais affiché.</p>
        <button type="button" class="btn btn-primary btn-sm" id="b-copy">Copier</button>`);

      $(sheet, '#b-issue')?.addEventListener('click', async (e) => {
        if (!armed(e.currentTarget as HTMLElement, 'Confirmer : l’ancien code ne marchera plus')) return;
        const res = await personAccess('issue', pid);
        if (!res.handle || !res.code) { toast(err(res.error), 'ko'); return; }
        showCode(res.handle, res.code);
        $(sheet, '#b-copy')!.addEventListener('click', async () => {
          try { await navigator.clipboard.writeText(`Arceau — identifiant : ${res.handle} · code : ${res.code}`); toast('Copié'); }
          catch { toast('Copie impossible : note-le à la main.', 'ko'); }
        });
        void reload();   // la liste derrière la fiche affiche le nouvel accès
      });
      const revoke = $<HTMLButtonElement>(sheet, '#b-revoke');
      if (revoke) confirmTwice(revoke, `Confirmer : ${name} ne pourra plus se connecter`, async () => {
        const res = await personAccess('revoke', pid);
        if (res.error) { toast(err(res.error), 'ko'); return; }
        close(); toast('Accès retiré'); await reload();
      });

      $(sheet, '#surc-chips')?.addEventListener('click', async (e) => {
        const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-surc]');
        if (!b) return;
        const c = b.dataset.surc!, x = surc.get(c);
        if (x && x.status !== 'demande' && !ctx.adminOf.some(a => a.id === team.club_id)) {
          toast(x.status === 'refuse' ? `Surclassement ${c} refusé cette saison.` : `Déjà ${SURC_STATUS[x.status].toLowerCase()} : seul le bureau peut le retirer.`, 'ko');
          return;
        }
        try {
          await setSurclassement(pid, team.season_id!, c, !x);
          await loadSurc();
          // Club avec un responsable technique (D-093) : la demande passe d'abord par lui.
          toast(x ? 'Demande retirée' : surc.get(c)?.status === 'technique'
            ? `Surclassement ${c} demandé : le responsable technique et le coach de la catégorie sont prévenus`
            : `Surclassement ${c} demandé : la famille et le bureau sont prévenus`);
          mount($(sheet, '#surc-chips')!, html`${surcOptions.map(surcChip)}`);
        } catch { toast('Enregistrement impossible.', 'ko'); }
      });
      // Le lien personnel (D-075) : relié à sa fiche, un seul usage, 7 jours.
      for (const b of $$<HTMLButtonElement>(sheet, '[data-plink]')) b.addEventListener('click', async () => {
        const rel = b.dataset.plink as 'self' | 'parent';
        try {
          const l = await personInviteLink(pid, rel);
          const url = links.invite(l.token);
          close();
          openShare(rel === 'parent' ? `Lien parent de ${name}` : `Lien de ${name}`, 'À envoyer en message privé : le lien est relié à sa fiche.', rel === 'parent'
            ? `Bonjour, voici votre accès à l’app du club pour suivre ${name} (${team.name}) : ${url} — vous donnez votre e-mail, vous recevez un code. Le lien ne sert qu’une fois.`
            : `Salut ${name} ! Ton accès à l’app des ${team.name} : ${url} — tu donnes ton e-mail, tu reçois un code, c’est tout. Le lien ne sert qu’une fois.`);
        } catch { toast('Lien impossible pour le moment.', 'ko'); }
      });
      const fm = $<HTMLFormElement>(sheet, '#f-mail');
      fm?.addEventListener('submit', async (e) => {
        e.preventDefault();
        const res = await invite('player', pid, String(new FormData(fm).get('email') ?? ''), undefined, undefined, undefined, links.app);
        if (res.error) { toast(err(res.error), 'ko'); return; }
        close(); toast(res.status === 'invited' ? 'Invitation envoyée' : 'Compte existant rattaché'); await reload();
      });
      const fp = $<HTMLFormElement>(sheet, '#f-parent');
      fp?.addEventListener('submit', async (e) => {
        e.preventDefault();
        const d = new FormData(fp);
        const res = await invite('parent', pid, String(d.get('email') ?? ''), String(d.get('relation') ?? ''), undefined, undefined, links.app);
        if (res.error) { toast(err(res.error), 'ko'); return; }
        close(); toast(res.status === 'invited' ? 'Invitation envoyée au parent' : 'Parent rattaché (compte existant)');
        await refreshContext(ctx); await reload();
      });
      const fe = $<HTMLFormElement>(sheet, '#f-edit');
      fe?.addEventListener('submit', async (e) => {
        e.preventDefault();
        const d = new FormData(fe);
        const first = String(d.get('first_name') ?? '').trim();
        if (!first) { toast('Le prénom est obligatoire.', 'ko'); return; }
        const jersey = String(d.get('jersey') ?? '');
        try {
          await updatePerson(pid, {
            first_name: first, last_name: String(d.get('last_name') ?? '').trim() || null,
            nickname: String(d.get('nickname') ?? '').trim() || null, birth_date: String(d.get('birth_date') ?? '') || null,
          });
          await updateMembership(teamId, pid, { jersey_number: jersey === '' ? null : Number(jersey) });
          close(); toast('Enregistré'); await reload();
        } catch { toast('Enregistrement impossible.', 'ko'); }
      });
      const leave = $<HTMLButtonElement>(sheet, '#b-leave');
      if (leave) confirmTwice(leave, `Confirmer : ${name} quitte l'effectif (sa fiche est gardée)`, async () => {
        try { await updateMembership(teamId, pid, { left_on: today }); close(); toast(`${agree(team.gender, 'Retiré', 'Retirée')} de l'effectif`); await reload(); }
        catch { toast('Impossible pour le moment.', 'ko'); }
      });
    });
  };

  for (const row of $$(root, '[data-open]')) {
    const go = () => openPlayer(row.dataset.open!);
    row.addEventListener('click', go);
    row.addEventListener('keydown', (e) => { if (e.key === 'Enter') go(); });
  }
  // #/t/<équipe>/fiche : la carte de la première joueuse (visite « La fiche pour les tirages », D-176).
  if (open) { const pid = open === 'first' ? manageable[0] : manageable.find(x => x === open); if (pid) void openPlayer(pid); }

  // Équipes engagées : qui joue où (leurs compétitions : « Compétitions de la saison », D-090).
  const allSq = squads.map(q => q.id);
  for (const b of $$<HTMLButtonElement>(root, '[data-sq]')) {
    b.addEventListener('click', async (e) => {
      e.stopPropagation();
      const r = roster.find(x => x.person_id === b.dataset.sqp)!;
      const next = toggleSquad(r.squad_ids ?? [], b.dataset.sq!, allSq);
      b.disabled = true;
      try { await setMemberSquads(teamId, r.person_id, next); r.squad_ids = next; await reload(); }
      catch { b.disabled = false; toast('Impossible pour le moment.', 'ko'); }
    });
    b.addEventListener('keydown', (e) => e.stopPropagation());
  }
  const fsq = $<HTMLFormElement>(root, '#f-squad');
  fsq?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const name = String(new FormData(fsq).get('name') ?? '').trim();
    if (!name) return;
    try { await createSquad(teamId, name, squads.length); await refreshContext(ctx); toast(`Équipe ${name} ajoutée`); await reload(); }
    catch { toast('Ce nom existe déjà dans le groupe.', 'ko'); }
  });
  for (const b of $$<HTMLButtonElement>(root, '[data-sq-del]')) {
    confirmTwice(b, 'confirmer', async () => {
      try { await deleteSquad(b.dataset.sqDel!); await refreshContext(ctx); toast('Équipe retirée : ses matchs reviennent au groupe'); await reload(); }
      catch { toast('Impossible pour le moment.', 'ko'); }
    });
  }
}

/** Les contacts d'une joueuse et de ses parents, pour le staff (D-103) : appeler, écrire, d'un geste. */
export function contactsBlock(c: Contacts, g: TeamGender): Html {
  const tel = (n: string) => `tel:${n.replace(/[^0-9+]/g, '')}`;
  const acts = (phone: string | null, emails: string[]) => phone || emails.length
    ? html`<span class="ct-acts">${phone ? html`<a class="ct-act" href="${tel(phone)}">📞 ${phone}</a>` : ''}${emails.map(e => html`<a class="ct-act" href="mailto:${e}">✉️ ${e}</a>`)}</span>`
    : html`<span class="ct-none">Ni téléphone ni e-mail</span>`;
  return html`<section class="sheet-sec ct-sec">
    <h4>Contacts</h4>
    <ul class="ct-list">
      <li class="ct-row"><span class="ct-who"><strong>${c.first_name}</strong><small>${c.minor ? agree(g, 'mineur', 'mineure') : playerNoun(g)}</small></span>${acts(c.phone, c.emails)}</li>
      ${c.parents.map(p => html`<li class="ct-row"><span class="ct-who"><strong>${p.name || 'Parent'}</strong><small>${[p.relation || 'parent', p.account ? 'dans l’app' : 'pas encore de compte'].join(' · ')}</small></span>${acts(p.phone, p.email ? [p.email] : [])}</li>`)}
    </ul>
    ${!c.parents.length && c.minor ? html`<p class="hint">Aucun contact de parent : complète-le dans l’import FBI, ou envoie le lien parent.</p>` : ''}
    <p class="hint">Visible du staff de l’équipe seulement (coachs, responsable technique, bureau), jamais des autres familles.</p>
  </section>`;
}

export type { RosterEntry };
